<template>
  <bPanel
    title="Banking Institutions"
    tag="article"
    class="mt-3"
    maxWidth="100%"
  >
    <div v-if="loading" class="text-center my-4">
      <BSpinner label="Loading..." />
    </div>

    <div v-else-if="fetchError" class="text-center my-4 text-danger">
      <IconAlertTriangle class="me-1 text-danger" />
      <br />
      Unable to load banking institutions for this account.
    </div>

    <div v-else-if="bankingInstitutions.length === 0" class="text-center my-4">
      <IconAlertTriangle class="me-1 text-warning" />
      <br />
      No banking institutions found for this account.
    </div>

    <ul v-else class="list-group mt-3">
      <li
        v-for="institution in bankingInstitutions"
        :key="institution.uid"
        class="list-group-item"
      >
        <div class="d-flex justify-content-between align-items-start gap-2">
          <div>
            <div class="fw-semibold">{{ institution.name }}</div>
            <div class="text-muted small">Type: {{ institution.type }}</div>
            <div class="text-muted small">
              Last 4: {{ institution.acct_last_4 }}
            </div>
            <div class="text-muted small">UID: {{ institution.uid }}</div>
            <div class="text-muted small">
              Created At: {{ formatDate(institution.created_at) }}
            </div>
          </div>
          <BButton
            size="sm"
            variant="outline-primary"
            :disabled="uploadingInstitutionUid === institution.uid"
            @click="openUploadDialog(institution)"
          >
            {{
              uploadingInstitutionUid === institution.uid
                ? "Parsing..."
                : "Upload Transactions"
            }}
          </BButton>
        </div>
      </li>
    </ul>

    <input
      ref="transactionsFileInput"
      type="file"
      class="d-none"
      accept=".csv,.xlsx,.xls"
      @change="onTransactionsFileSelected"
    />
  </bPanel>
</template>

<script>
import { DateTime } from "luxon";
import { mapActions } from "vuex";
import api from "@/services/api";
import { BButton, BSpinner } from "bootstrap-vue-next";
import { IconAlertTriangle } from "@tabler/icons-vue";
import bPanel from "@/components/atoms/bPanel.vue";
import { parseInstitutionTransactionsFile } from "@/services/transactionsImport";
import { userSession as userSessNs } from "@/store/namespaces";

export default {
  name: "ListView",
  components: {
    bPanel,
    BButton,
    BSpinner,
    IconAlertTriangle,
  },
  props: {
    acctUid: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loading: false,
      fetchError: false,
      bankingInstitutions: [],
      selectedInstitutionForUpload: null,
      uploadingInstitutionUid: null,
    };
  },
  watch: {
    acctUid: {
      immediate: true,
      handler() {
        this.fetchBankingInstitutions();
      },
    },
  },
  methods: {
    ...mapActions({
      getUserInfo: `${userSessNs}/getUserInfo`,
    }),
    async fetchBankingInstitutions() {
      if (!this.acctUid) {
        this.bankingInstitutions = [];
        this.fetchError = false;
        return;
      }

      try {
        this.loading = true;
        this.fetchError = false;

        const response = await api.get("/banking-institutions", {
          params: { filter: [`acctUid:eq:${this.acctUid}`] },
        });

        this.bankingInstitutions = Array.isArray(response?.data)
          ? response.data.map((entry) => entry.item)
          : [];
      } catch (error) {
        this.bankingInstitutions = [];
        this.fetchError = true;
        console.error("Error fetching banking institutions:", error);
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateValue) {
      if (!dateValue) return "N/A";
      const parsed = DateTime.fromISO(dateValue);
      return parsed.isValid ? parsed.toFormat("yyyy-MM-dd HH:mm") : "N/A";
    },
    openUploadDialog(institution) {
      this.selectedInstitutionForUpload = institution;

      const fileInput = this.$refs.transactionsFileInput;
      if (!fileInput) {
        return;
      }

      fileInput.value = "";
      fileInput.click();
    },
    warnParseErrors(institution, file, errors) {
      if (!errors?.length) {
        return;
      }

      const errorMessages = errors
        .map((e) => `Row ${e.rowNumber}: ${e.message}`)
        .join("\n");
      console.error("Transaction parse errors", {
        institutionUid: institution.uid,
        institutionName: institution.name,
        fileName: file.name,
        errors,
      });
      this.$notify.notifyWarning(
        "Some transactions could not be imported",
        `${errors.length} row(s) had errors and were skipped:\n${errorMessages}`
      );
    },
    async onTransactionsFileSelected(event) {
      const file = event?.target?.files?.[0];
      if (!file || !this.selectedInstitutionForUpload) {
        return;
      }

      const institution = this.selectedInstitutionForUpload;
      this.uploadingInstitutionUid = institution.uid;

      try {
        const parseResult = await parseInstitutionTransactionsFile({
          file,
          institutionName: institution.name,
          detailsToInclude: [],
        });

        this.warnParseErrors(institution, file, parseResult.errors);

        const userInfo = await this.getUserInfo();
        const userUid = userInfo?.id;

        if (!userUid) {
          throw new Error("Missing user UID for transaction upload.");
        }

        console.log("Uploading transactions", {
          institutionUid: institution.uid,
          institutionName: institution.name,
          fileName: file.name,
          transactions: parseResult.transactions,
        });

        const response = await api.post(
          `/banking-institutions/${institution.uid}/transactions`,
          {
            acct_uid: this.acctUid,
            user_uid: userUid,
            institution_name: institution.name,
            transactions: parseResult.transactions,
          }
        );

        this.$notify.notifySuccess(
          "Transactions Uploaded",
          `Successfully uploaded ${response.data.item.total_transactions_created} transactions for ${institution.name}.`
        );
        if (response.data.item.total_transactions_failed > 0) {
          this.$notify.notifyError(
            "Some Transactions Failed",
            `${response.data.item.total_transactions_failed} transactions could not be uploaded. Please check the file format and try again.`
          );
        }
      } catch (error) {
        console.error("Unable to upload transactions file", {
          institutionUid: institution.uid,
          institutionName: institution.name,
          fileName: file.name,
          error,
        });
        this.$notify.notifyError(
          "Transaction Upload Failed",
          `Failed to upload transactions for ${institution.name}. Please try again.`
        );
      } finally {
        this.uploadingInstitutionUid = null;
      }
    },
  },
};
</script>
